import { useMemo } from "react";
import { css } from "glamor";
import { InfoTooltip } from "./InfoTooltip";

export interface MaterializationViewStatus {
  id: string;
  name: string;
  sync_frequency: string | null;
  status: string | null;
  last_run_at: string | null;
  latest_error: string | null;
  is_materialized: boolean;
}

interface MaterializationStatusTileProps {
  views?: MaterializationViewStatus[];
}

type ViewHealth = "ok" | "warn" | "bad";
type OverallHealth = "good" | "warning" | "bad";

const classifyViewHealth = (view: MaterializationViewStatus): ViewHealth => {
  const { status, latest_error, is_materialized } = view;
  const normalized = (status || "").toLowerCase();

  // Non-materialized views (e.g. v_activation_vars) are always up to date; treat as healthy
  if (is_materialized === false) {
    return "ok";
  }

  if (normalized === "completed" || normalized === "success") {
    return "ok";
  }

  if (["failed", "error", "cancelled"].includes(normalized) || latest_error) {
    return "bad";
  }

  if (
    !status ||
    ["never_run", "pending", "scheduled", "starting"].includes(normalized)
  ) {
    return "warn";
  }

  return "ok";
};

const deriveOverallHealth = (views: MaterializationViewStatus[]): OverallHealth => {
  if (!views.length) return "warning";

  let hasWarn = false;
  for (const v of views) {
    const h = classifyViewHealth(v);
    if (h === "bad") return "bad";
    if (h === "warn") hasWarn = true;
  }
  return hasWarn ? "warning" : "good";
};

const formatLastRun = (value: string | null): string => {
  if (!value) return "Never";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString();
};

const healthLabel: Record<OverallHealth, string> = {
  good: "All up to date",
  warning: "Some views need attention",
  bad: "Materialization issues detected",
};

export const MaterializationStatusTile = ({ views }: MaterializationStatusTileProps) => {
  const overallHealth: OverallHealth = useMemo(
    () => deriveOverallHealth(views || []),
    [views]
  );

  const headerDotColor =
    overallHealth === "good" ? "var(--green)" : overallHealth === "warning" ? "var(--amber)" : "var(--red)";

  const hasData = !!views && views.length > 0;

  return (
    <div
      {...css({
        background: "var(--s1)",
        borderRadius: 12,
        boxShadow: "var(--shadow)",
        border: "1px solid var(--brd)",
        padding: 24,
        display: "flex",
        flexDirection: "column",
        gap: 12,
      })}
    >
      <div
        {...css({
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 8,
        })}
      >
        <div
          {...css({
            display: "flex",
            alignItems: "center",
            gap: 8,
            flexWrap: "wrap",
          })}
        >
          <div
            {...css({
              width: 8,
              height: 8,
              borderRadius: "50%",
              background: headerDotColor,
            })}
          />
          <div
            {...css({
              display: "flex",
              flexDirection: "column",
              gap: 2,
            })}
          >
            <h3
              {...css({
                fontSize: 15,
                fontWeight: 500,
                color: "var(--t1)",
                margin: 0,
              })}
            >
              Warehouse materialization
            </h3>
            <span
              {...css({
                fontSize: 11,
                color:
                  overallHealth === "good"
                    ? "var(--green)"
                    : overallHealth === "warning"
                    ? "var(--amber)"
                    : "var(--red)",
              })}
            >
              {healthLabel[overallHealth]}
            </span>
          </div>
        </div>
        <InfoTooltip text="Status of PLH materialized views in PostHog warehouse, including last run and any errors. If this is red, PLH metrics may be stale." />
      </div>

      {!hasData && (
        <div
          {...css({
            fontSize: 13,
            color: "var(--t3)",
          })}
        >
          No PLH materialized views found.
        </div>
      )}

      {hasData && (
        <div
          {...css({
            marginTop: 4,
            borderTop: "1px solid var(--brd)",
            paddingTop: 8,
            display: "flex",
            flexDirection: "column",
            gap: 8,
          })}
        >
          <div
            {...css({
              display: "grid",
              gridTemplateColumns: "minmax(0, 2.2fr) minmax(0, 1.2fr) minmax(0, 1.6fr) minmax(0, 1fr)",
              columnGap: 8,
              rowGap: 4,
              fontSize: 11,
              color: "var(--t3)",
              textTransform: "uppercase",
              letterSpacing: 0.4,
            })}
          >
            <span>View</span>
            <span>Status</span>
            <span>Last run</span>
            <span>Frequency</span>
          </div>
          <div
            {...css({
              maxHeight: 220,
              overflowY: "auto",
              paddingRight: 4,
            })}
          >
            {views!.map((view) => {
              const vHealth = classifyViewHealth(view);
              const pillClass =
                vHealth === "ok"
                  ? "plh-status-pill plh-status-pill--ok"
                  : vHealth === "warn"
                  ? "plh-status-pill plh-status-pill--warn"
                  : "plh-status-pill plh-status-pill--bad";

              return (
                <div
                  key={view.id}
                  {...css({
                    display: "grid",
                    gridTemplateColumns:
                      "minmax(0, 2.2fr) minmax(0, 1.2fr) minmax(0, 1.6fr) minmax(0, 1fr)",
                    columnGap: 8,
                    alignItems: "center",
                    padding: "4px 0",
                    fontSize: 12,
                    color: "var(--t1)",
                  })}
                >
                  <span
                    {...css({
                      fontFamily: "var(--mono, 'SF Mono', Menlo, monospace)",
                      fontSize: 11,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    })}
                    title={view.name}
                  >
                    {view.name}
                  </span>
                  <span className={pillClass}>
                    {view.is_materialized === false
                      ? "COMPLETED"
                      : view.status ?? "unknown"}
                  </span>
                  <span
                    {...css({
                      fontSize: 11,
                      color: "var(--t3)",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    })}
                    title={formatLastRun(view.last_run_at)}
                  >
                    {formatLastRun(view.last_run_at)}
                  </span>
                  <span
                    {...css({
                      fontSize: 11,
                      color: "var(--t3)",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    })}
                    title={view.sync_frequency || ""}
                  >
                    {view.sync_frequency || "—"}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

